import { Alert, Box, Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import type { TaxComponents } from '../../data/types';

const FIELDS: { key: keyof TaxComponents; label: string; help: string }[] = [
  { key: 'interest', label: 'Interest', help: 'Assessable; subject to non-resident withholding' },
  { key: 'otherIncome', label: 'Other Australian income', help: 'Rent, fees and other net income' },
  {
    key: 'discountedGain',
    label: 'Discounted capital gain',
    help: 'Grossed-up gain eligible for the CGT discount',
  },
  {
    key: 'taxDeferred',
    label: 'Tax-deferred (AMIT cost base reduction)',
    help: 'Reduces the holder’s cost base',
  },
  { key: 'foreignIncome', label: 'Foreign income', help: 'With any foreign income tax offset' },
];

const PRESETS: Record<string, TaxComponents> = {
  'Senior debt (mostly interest)': {
    interest: 0.92,
    otherIncome: 0.05,
    discountedGain: 0,
    taxDeferred: 0.03,
    foreignIncome: 0,
  },
  'Property income trust': {
    interest: 0.1,
    otherIncome: 0.55,
    discountedGain: 0.05,
    taxDeferred: 0.3,
    foreignIncome: 0,
  },
  'Equity / JV returns': {
    interest: 0.35,
    otherIncome: 0.4,
    discountedGain: 0.2,
    taxDeferred: 0.05,
    foreignIncome: 0,
  },
};

export default function ComponentsEditor({
  value,
  onChange,
}: {
  value: TaxComponents;
  onChange: (next: TaxComponents) => void;
}) {
  const total = Object.values(value).reduce((sum, item) => sum + item, 0);
  return (
    <>
      <Typography variant="h6">Tax components</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Estimated components drive withholding now and the AMMA statement at year end. In production
        these are imported from the fund accountant&apos;s workbook or Xero.
      </Typography>
      <Stack useFlexGap direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {Object.entries(PRESETS).map(([label, preset]) => (
          <Button key={label} size="small" variant="outlined" onClick={() => onChange(preset)}>
            {label}
          </Button>
        ))}
      </Stack>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {FIELDS.map((field) => (
          <TextField
            key={field.key}
            label={field.label}
            type="number"
            value={Math.round(value[field.key] * 1000) / 10}
            onChange={(event) =>
              onChange({ ...value, [field.key]: (Number(event.target.value) || 0) / 100 })
            }
            helperText={field.help}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        ))}
      </Box>
      <Alert severity={Math.abs(total - 1) < 0.0001 ? 'success' : 'error'} sx={{ mt: 2 }}>
        Components total {(total * 100).toFixed(1)}%
        {Math.abs(total - 1) < 0.0001 ? '' : ' — they must add up to 100%'}
      </Alert>
    </>
  );
}
